import React, { useState, useEffect } from 'react';
import { Sparkles, Plus, Search, Building2, Calendar, Users, TrendingUp, Filter, X, Edit2, Trash2, MapPin, Phone, Mail, Globe, Star, Percent } from 'lucide-react';
import { supabase } from '../lib/supabase';
import NewImageConsultantProviderModal from '../components/NewImageConsultantProviderModal';
import NewImageConsultantServiceModal from '../components/NewImageConsultantServiceModal';
import NewImageConsultantAppointmentModal from '../components/NewImageConsultantAppointmentModal';

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  display_order: number;
}

interface Provider {
  id: string;
  name: string;
  category_id: string;
  business_name: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  discount_percentage: number;
  notes: string;
  rating: number;
  is_active: boolean;
  category?: Category;
}

interface Service {
  id: string;
  category_id: string;
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
  notes: string;
  category?: Category;
}

interface Appointment {
  id: string;
  contact_id: string;
  provider_id: string;
  service_id: string | null;
  appointment_date: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  price_paid: number;
  notes: string;
  contact?: any;
  provider?: Provider;
  service?: Service;
}

const ConsulenteDImmagine: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'providers' | 'services' | 'appointments'>('providers');
  const [categories, setCategories] = useState<Category[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);
  const [showNewServiceModal, setShowNewServiceModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [categoriesData, providersData, servicesData, appointmentsData] = await Promise.all([
        supabase.from('image_consultant_categories').select('*').order('display_order'),
        supabase.from('image_consultant_providers').select(`
          *,
          category:image_consultant_categories(*)
        `).order('name'),
        supabase.from('image_consultant_services').select(`
          *,
          category:image_consultant_categories(*)
        `).order('name'),
        supabase.from('image_consultant_appointments').select(`
          *,
          contact:contacts(*),
          provider:image_consultant_providers(*),
          service:image_consultant_services(*)
        `).order('appointment_date', { ascending: false })
      ]);

      if (categoriesData.data) setCategories(categoriesData.data);
      if (providersData.data) setProviders(providersData.data);
      if (servicesData.data) setServices(servicesData.data);
      if (appointmentsData.data) setAppointments(appointmentsData.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    const icons: Record<string, any> = {
      sparkles: Sparkles,
      shirt: Users,
      smile: Star,
      scissors: Edit2,
      apple: TrendingUp,
      dumbbell: TrendingUp,
    };
    const Icon = icons[iconName] || Sparkles;
    return <Icon className="h-5 w-5" />;
  };

  const getColorClass = (color: string) => {
    const colors: Record<string, string> = {
      pink: 'bg-pink-100 text-pink-700 border-pink-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      teal: 'bg-teal-100 text-teal-700 border-teal-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      red: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[color] || colors.pink;
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          provider.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          provider.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || provider.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || service.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = {
    totalProviders: providers.length,
    activeProviders: providers.filter(p => p.is_active).length,
    totalServices: services.length,
    upcomingAppointments: appointments.filter(a => a.status === 'scheduled' && new Date(a.appointment_date) > new Date()).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">Consulente d'Immagine</h1>
            <p className="text-white/80 mt-1">Gestisci servizi e fornitori per il miglioramento dell'immagine</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Fornitori Totali</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.totalProviders}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Fornitori Attivi</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.activeProviders}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Servizi Disponibili</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.totalServices}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Appuntamenti</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.upcomingAppointments}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('providers')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'providers'
                    ? 'bg-rose-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Building2 className="h-4 w-4 inline-block mr-2" />
                Fornitori
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'services'
                    ? 'bg-rose-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Sparkles className="h-4 w-4 inline-block mr-2" />
                Servizi
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'appointments'
                    ? 'bg-rose-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Calendar className="h-4 w-4 inline-block mr-2" />
                Appuntamenti
              </button>
            </div>
            <button
              onClick={() => {
                if (activeTab === 'providers') setShowNewProviderModal(true);
                else if (activeTab === 'services') setShowNewServiceModal(true);
                else setShowNewAppointmentModal(true);
              }}
              className="flex items-center gap-2 px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium shadow-sm whitespace-nowrap"
            >
              <Plus className="h-5 w-5" />
              Nuovo
            </button>
          </div>

          <div className="px-6 pb-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-rose-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tutte
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    selectedCategory === category.id
                      ? 'bg-rose-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getIconComponent(category.icon)}
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'providers' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProviders.map((provider) => (
                <div
                  key={provider.id}
                  className={`border-2 rounded-xl p-4 transition-all hover:shadow-lg ${
                    provider.is_active ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                      {provider.business_name && (
                        <p className="text-sm text-gray-600">{provider.business_name}</p>
                      )}
                    </div>
                    {provider.category && (
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getColorClass(provider.category.color)}`}>
                        {provider.category.name}
                      </span>
                    )}
                  </div>

                  {provider.discount_percentage > 0 && (
                    <div className="mb-3 flex items-center gap-2 text-green-600 bg-green-50 rounded-lg px-3 py-2">
                      <Percent className="h-4 w-4" />
                      <span className="text-sm font-medium">Sconto {provider.discount_percentage}%</span>
                    </div>
                  )}

                  <div className="space-y-2 text-sm">
                    {provider.city && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="h-4 w-4" />
                        {provider.city}
                      </div>
                    )}
                    {provider.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="h-4 w-4" />
                        {provider.phone}
                      </div>
                    )}
                    {provider.email && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4" />
                        {provider.email}
                      </div>
                    )}
                    {provider.website && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Globe className="h-4 w-4" />
                        <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                          Sito web
                        </a>
                      </div>
                    )}
                  </div>

                  {provider.rating > 0 && (
                    <div className="mt-3 flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < provider.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-sm text-gray-600 ml-1">({provider.rating})</span>
                    </div>
                  )}

                  {!provider.is_active && (
                    <div className="mt-3 text-xs text-gray-500 font-medium">
                      Non attivo
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'services' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredServices.map((service) => (
                <div key={service.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{service.name}</h3>
                      {service.description && (
                        <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                      )}
                    </div>
                    {service.category && (
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getColorClass(service.category.color)}`}>
                        {service.category.name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="font-semibold text-pink-600">€{service.price}</span>
                      <span className="text-gray-600">{service.duration_minutes} min</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'appointments' && (
            <div className="space-y-3">
              {appointments.map((appointment) => (
                <div key={appointment.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          {appointment.contact?.name || 'Cliente sconosciuto'}
                        </h3>
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          appointment.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          appointment.status === 'completed' ? 'bg-green-100 text-green-700' :
                          appointment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {appointment.status === 'scheduled' ? 'Programmato' :
                           appointment.status === 'completed' ? 'Completato' :
                           appointment.status === 'cancelled' ? 'Cancellato' : 'No Show'}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div>Fornitore: {appointment.provider?.name}</div>
                        {appointment.service && <div>Servizio: {appointment.service.name}</div>}
                        <div>Data: {new Date(appointment.appointment_date).toLocaleString('it-IT')}</div>
                        {appointment.price_paid > 0 && <div>Prezzo: €{appointment.price_paid}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'providers' && filteredProviders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nessun fornitore trovato
            </div>
          )}

          {activeTab === 'services' && filteredServices.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nessun servizio trovato
            </div>
          )}

          {activeTab === 'appointments' && appointments.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nessun appuntamento trovato
            </div>
          )}
        </div>
      </div>

      <NewImageConsultantProviderModal
        isOpen={showNewProviderModal}
        onClose={() => setShowNewProviderModal(false)}
        onSuccess={loadData}
      />

      <NewImageConsultantServiceModal
        isOpen={showNewServiceModal}
        onClose={() => setShowNewServiceModal(false)}
        onSuccess={loadData}
      />

      <NewImageConsultantAppointmentModal
        isOpen={showNewAppointmentModal}
        onClose={() => setShowNewAppointmentModal(false)}
        onSuccess={loadData}
      />
    </div>
  );
};

export default ConsulenteDImmagine;
